import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Wallet, X, ExternalLink, RefreshCw, ChevronLeft, ChevronRight, CalendarClock, CalendarPlus } from 'lucide-react';
import { format, addDays, startOfDay } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useActiveCall } from '@/hooks/useActiveCall';
import { placeBookingFromDeposit, resendDepositLink, isEffectivelyExpired } from '@/domain/depositFlow';
import { confirmGapFillOnCall, type GapFillDepositMethod } from '@/domain/gapFillFlow';
import { routeExpiredResend } from '@/domain/resendRoutingFlow';
import { useUIStore } from '@/state/uiStore';
import { hourToIso } from '@/domain/depositWindow';
import { markSaidNo } from '@/domain/sequenceFlow';
import { rescheduleBooking } from '@/domain/rescheduleFlow';
import { buildHeatmap, type QueueItem, type HeatmapCell } from '@/domain/queueEngine';
import { artistMatchesStyle } from '@/domain/availabilityScope';
import { recommendChannel } from '@/domain/aiExtract';
import { TATTOO_STYLES } from '@/domain/types';
import type { DesignIntent, Piece } from '@/domain/types';
import { customerName, aud, simTimeFull, simTimeShort } from '@/utils/format';
import { piecesForCustomer, pieceSessionLabel, pieceTitleOf } from '@/utils/pieceHelpers';
import ConversationGlance from '@/components/queue/ConversationGlance';
import SuggestedReplyCard from '@/components/queue/SuggestedReplyCard';
import CustomerComposer from '@/components/CustomerComposer';
import CustomerBriefControls from '@/components/CustomerBriefControls';
import DepositLinkModal from '@/components/DepositLinkModal';
import LiveCallBanner from '@/components/LiveCallBanner';
import ArtistAvailabilityHeatmap from '@/components/queue/ArtistAvailabilityHeatmap';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';

// Mirrors the composer's private channel union so we can forward the focused
// channel straight through without widening the type.
type MessageChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

// Which availability scope the availability pane is showing.
type ArtistScope = 'best' | 'all';

export interface GapTarget {
  artistId: string;
  dayIso: string;
  startIso: string;
  endIso: string;
}

interface Props {
  item: QueueItem | null;
  onClose: () => void;
  initialChannel?: MessageChannel;
  /** When opened from a gap-fill surface: the slot being filled. Pre-pins the
   * availability pane and enables the bring-forward / place-from-deposit actions. */
  gap?: GapTarget;
}

/**
 * Three-pane "work-in-place" modal for a queue item. Renders nothing when no
 * item is set; otherwise mounts an inner component keyed by item id so local
 * working state (piece name, picked slot, outcome) resets cleanly per customer.
 */
export default function QueueWorkModal({ item, onClose, initialChannel, gap }: Props) {
  if (!item) return null;
  return (
    <QueueWorkModalInner
      key={item.id}
      item={item}
      onClose={onClose}
      initialChannel={initialChannel}
      gap={gap}
    />
  );
}

function cohortLine(cohort: QueueItem['cohort']): string {
  if (cohort === 'fresh') return 'Fresh inquiry';
  if (cohort === 'money') return 'Deposit received, no date locked in';
  if (cohort === 'open-business') return 'Open business';
  return '';
}

function cohortWord(cohort: QueueItem['cohort']): string {
  if (cohort === 'fresh') return 'Fresh';
  if (cohort === 'money') return 'Money';
  if (cohort === 'open-business') return 'Open business';
  return 'Sequence';
}

function stagePip(item: QueueItem): string | null {
  if (item.stage == null) return null;
  if (item.stage === 'red') {
    return item.redKind === 'said-no' ? 'Declined' : 'No answer x3';
  }
  return `${cohortWord(item.cohort)} - stage ${item.stage} of 3`;
}

function QueueWorkModalInner({
  item,
  onClose,
  initialChannel,
  gap,
}: {
  item: QueueItem;
  onClose: () => void;
  initialChannel?: MessageChannel;
  gap?: GapTarget;
}) {
  const customerId = item.customer.id;
  const navigate = useNavigate();
  const clockIso = useSimulationStore((s) => s.clockIso);

  // Read the live customer from the store so persisted preferences and freshly
  // sent messages surface immediately; fall back to the queue snapshot.
  const liveCustomer = useBrainStore((s) => s.customers[customerId]) ?? item.customer;
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const studios = useBrainStore((s) => s.studios);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const addRapportNote = useBrainStore((s) => s.addRapportNote);
  const updateDesignIntent = useBrainStore((s) => s.updateDesignIntent);
  const setCustomerPreferences = useBrainStore((s) => s.setCustomerPreferences);
  const persona = useSessionStore((s) => s.persona);

  const activeCall = useActiveCall();
  const callForThisCustomer = activeCall?.customerId === customerId ? activeCall : null;

  const [selection, setSelection] = useState<{ artistId: string; dayIso: string } | null>(
    gap ? { artistId: gap.artistId, dayIso: gap.dayIso } : null,
  );
  const [pinnedSlot, setPinnedSlot] = useState<{
    artistId: string;
    startIso: string;
    endIso: string;
  } | null>(gap ? { artistId: gap.artistId, startIso: gap.startIso, endIso: gap.endIso } : null);
  const [depositOpen, setDepositOpen] = useState(false);
  // tv-pt8: a gap-fill is always confirmed on the call; the operator's only
  // choice is how the deposit is taken.
  const [gapFillMethod, setGapFillMethod] = useState<GapFillDepositMethod>('phone-eftpos');
  const [showOutcome, setShowOutcome] = useState(false);
  const [outcomeNote, setOutcomeNote] = useState('');
  // "Edit first" on a suggested reply hands its body down to the composer.
  const [composerDraft, setComposerDraft] = useState<string | undefined>(undefined);
  // Picking a piece fills the brief below, so scroll the rail back up to it.
  const briefRailRef = useRef<HTMLElement>(null);

  // Availability-pane controls: one-week window, artist scope, and a refresh
  // stamp/nonce. weekStartIso is day-normalised so stepping stays on day bounds.
  const [weekStartIso, setWeekStartIso] = useState(() =>
    startOfDay(new Date(gap ? gap.dayIso : clockIso)).toISOString(),
  );
  const [scope, setScope] = useState<ArtistScope>('best');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [updatedIso, setUpdatedIso] = useState(clockIso);

  // Modal layout is a session-persisted UI preference, not per-item state, so a
  // chosen arrangement survives the inner component remounting per queue item.
  const layout = useUIStore((s) => s.queueModalLayout);
  const setLayout = useUIStore((s) => s.setQueueModalLayout);
  // Focus layout only: which tool the single right-hand panel is showing. Opens
  // on availability when filling a gap (the slot is the whole job), else on the
  // brief.
  const [toolTab, setToolTab] = useState<'brief' | 'availability'>(gap ? 'availability' : 'brief');

  // Conversation length at open - a later growth means the agent reached out.
  // Covers both paths: a sent message appends immediately, and a call appends its
  // voice entry on hang-up, which is when an outcome is worth recording anyway.
  const initialConvoLen = useRef(liveCustomer.conversation.length);
  const contacted = liveCustomer.conversation.length > initialConvoLen.current;

  // Channel everything in this column agrees on. When the host hasn't forced one,
  // use the same recommendation the glance card prints as "Best channel" - the
  // composer's own inference is the last messageable channel, which for a record
  // whose newest entry is an outbound waiver email meant the glance advised SMS
  // while the composer and the send button both said email.
  const suggestChannel: MessageChannel = useMemo(() => {
    if (initialChannel) return initialChannel;
    const rec = recommendChannel(liveCustomer).channel;
    if (rec === 'sms' || rec === 'meta-dm' || rec === 'ig-dm' || rec === 'email') return rec;
    // Recommendation is voice or a non-messageable channel: fall back to the last
    // channel they were actually messaged on.
    const last = [...liveCustomer.conversation]
      .reverse()
      .find(
        (e) =>
          e.channel === 'sms' ||
          e.channel === 'meta-dm' ||
          e.channel === 'ig-dm' ||
          e.channel === 'email',
      );
    return (last?.channel as MessageChannel | undefined) ?? 'sms';
  }, [initialChannel, liveCustomer]);

  // Studio in scope for the heatmap: the customer's preferred studio when set,
  // else their primary. The brief controls own the artist/studio selectors.
  const studioForArtists = liveCustomer.preferredStudioId ?? liveCustomer.primaryStudioId;

  // One week of availability for the in-scope studio. Windowed by weekStartIso;
  // refreshNonce lets Refresh re-read live store state mid-call.
  const heatmapRows = useMemo(
    () =>
      buildHeatmap({
        artists,
        bookings,
        nowIso: clockIso,
        studioIds: [gap ? (artists[gap.artistId]?.studioId ?? studioForArtists) : studioForArtists],
        startIso: weekStartIso,
        days: 7,
      }),
    // refreshNonce is an intentional recompute trigger (re-reads live store state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artists, bookings, clockIso, studioForArtists, weekStartIso, refreshNonce, gap],
  );

  const preferredStyle = liveCustomer.preferredStyle;
  const preferredArtistId = liveCustomer.preferredArtistId;

  // Best fit falls back to All only when there's nothing to scope by.
  const bestFitFallsBackToAll = !preferredStyle && !preferredArtistId;

  // Best-fit rows: in-scope-studio artists matching the preferred style, unioned
  // with the preferred artist. With no style set, it's just the preferred artist.
  const bestFitRows = useMemo(() => {
    const ids = new Set<string>();
    for (const row of heatmapRows) {
      if (preferredStyle && artistMatchesStyle(row.artist, preferredStyle)) {
        ids.add(row.artist.id);
      }
    }
    if (preferredArtistId) ids.add(preferredArtistId);
    return heatmapRows.filter((r) => ids.has(r.artist.id));
  }, [heatmapRows, preferredStyle, preferredArtistId]);

  // Filling a specific gap: the availability pane is that one artist's week only.
  const scopedRows = gap
    ? heatmapRows.filter((r) => r.artist.id === gap.artistId)
    : scope === 'all' || bestFitFallsBackToAll || (scope === 'best' && bestFitRows.length === 0)
      ? heatmapRows
      : bestFitRows;

  const studioLabelForNote = studios[studioForArtists]?.name ?? studioForArtists;
  const scopeNote = gap
    ? `${artists[gap.artistId]?.displayName ?? gap.artistId} only, the artist for this gap.`
    : scope === 'all'
      ? `All artists at ${studioLabelForNote}.`
      : bestFitFallsBackToAll
        ? `Best fit - no style or artist preference yet, showing all artists at ${studioLabelForNote}.`
        : `Best fit - ${
            preferredStyle ? `${preferredStyle.replace('-', ' ')} artists` : 'the preferred artist'
          } at ${studioLabelForNote}${
            preferredArtistId && artists[preferredArtistId]
              ? `, incl. ${artists[preferredArtistId]!.displayName}`
              : ''
          }.`;

  const weekStart = new Date(weekStartIso);
  const weekEnd = addDays(weekStart, 6);
  const weekRangeLabel = `${format(weekStart, 'd MMM')} - ${format(weekEnd, 'd MMM')}`;
  const stepWeek = (deltaWeeks: number) =>
    setWeekStartIso(addDays(new Date(weekStartIso), deltaWeeks * 7).toISOString());
  const onRefresh = () => {
    setRefreshNonce((n) => n + 1);
    setUpdatedIso(clockIso);
  };

  const customerBookings = liveCustomer.bookings
    .map((id) => bookings[id])
    .filter((b): b is NonNullable<typeof b> => Boolean(b));

  // History grouped by piece: an unfinished piece becomes one card the agent can
  // book the next sitting on. Pieces resolve by customerId (see pieceHelpers -
  // the customer.pieces back-link is unreliable), so a piece with no linked
  // sittings on this record still gets a card.
  const pieceGroups = useMemo(() => {
    const owned = piecesForCustomer(pieces, liveCustomer);
    const rows = owned.map((piece) => {
      const sittings = customerBookings
        .filter((b) => b.pieceId === piece.id)
        .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
      return {
        piece,
        latest: sittings[0],
        continuable: piece.status === 'in-progress' || piece.status === 'paused-will-return',
      };
    });
    // Continuable first, then most recently worked on.
    return rows.sort(
      (a, b) =>
        Number(b.continuable) - Number(a.continuable) ||
        new Date(b.latest?.start ?? b.piece.lastSessionDate ?? 0).getTime() -
          new Date(a.latest?.start ?? a.piece.lastSessionDate ?? 0).getTime(),
    );
  }, [pieces, liveCustomer, customerBookings]);

  const groupedPieceIds = new Set(pieceGroups.map((g) => g.piece.id));
  const looseBookings = customerBookings
    .filter((b) => !b.pieceId || !groupedPieceIds.has(b.pieceId))
    .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());

  const continuingPieceId =
    liveCustomer.designIntent?.pieceMode === 'continue'
      ? liveCustomer.designIntent.continuePieceId
      : undefined;

  // The rail is the only way into a continuation. Picking a piece stamps the
  // brief with its title and session estimate so the deposit and the booking
  // that follow describe the right piece; picking it again backs out.
  const toggleContinue = (piece: Piece) => {
    if (continuingPieceId === piece.id) {
      updateDesignIntent(customerId, {
        pieceMode: 'new',
        continuePieceId: undefined,
        newPieceTitle: undefined,
        estimatedSessions: undefined,
      });
      return;
    }

    // The next sitting on an existing piece is the same piece: same artist, same
    // placement, same look. Carry all of it across rather than making the agent
    // retype what the piece already knows. The most recent sitting supplies the
    // things that live on the booking rather than the piece.
    const latest = customerBookings
      .filter((b) => b.pieceId === piece.id)
      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];

    const patch: Partial<DesignIntent> = {
      pieceMode: 'continue',
      continuePieceId: piece.id,
      newPieceTitle: pieceTitleOf(piece),
      estimatedSessions: piece.estimatedTotalSessions,
      type: piece.designType,
      placement: piece.placement,
      // The piece's own references come across pre-ticked; anything already
      // attached stays.
      referencePhotos: [
        ...new Set([
          ...(liveCustomer.designIntent?.referencePhotos ?? []),
          ...(piece.referencePhotos ?? []),
        ]),
      ],
    };
    if (latest?.sessionLength) patch.size = latest.sessionLength;
    if (latest?.colour) patch.colour = latest.colour;
    if (latest?.bodyPart) patch.bodyPart = latest.bodyPart;
    if (latest?.bodyPartSide) patch.bodyPartSide = latest.bodyPartSide;
    if (latest?.bodyPartQualifier) patch.bodyPartQualifier = latest.bodyPartQualifier;
    updateDesignIntent(customerId, patch);

    // Artist and style are customer preferences, not design intent, and they also
    // scope the availability pane - so a continuation lands on the right artist's
    // week straight away.
    const prefs: Parameters<typeof setCustomerPreferences>[1] = {
      preferredArtistId: piece.assignedArtistId,
    };
    const style = TATTOO_STYLES.find((s) => s === piece.style.toLowerCase());
    if (style) prefs.preferredStyle = style;
    setCustomerPreferences(customerId, prefs);

    // The fields that just changed are above the rail, so take them there.
    // jsdom implements scrollTop but not scrollTo, hence the fallback.
    const rail = briefRailRef.current;
    if (typeof rail?.scrollTo === 'function') rail.scrollTo({ top: 0, behavior: 'smooth' });
    else if (rail) rail.scrollTop = 0;
  };

  // Stable close handler for Escape without re-binding the listener each render.
  const requestCloseRef = useRef(() => {});
  requestCloseRef.current = () => {
    if (showOutcome) {
      onClose();
      return;
    }
    // Only the 3rd-sequence contact offers a manual outcome (decline). Stages 1-2
    // auto-advance (the attempt is recorded by the call/message itself).
    if (contacted && item.stage === 3) setShowOutcome(true);
    else onClose();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onSelectCell = (cell: HeatmapCell) => {
    setSelection({ artistId: cell.artistId, dayIso: cell.dayIso });
  };

  const onPickWindow = (w: { startHour: number; endHour: number }) => {
    if (!selection) return;
    setPinnedSlot({
      artistId: selection.artistId,
      startIso: hourToIso(selection.dayIso, w.startHour),
      endIso: hourToIso(selection.dayIso, w.endHour),
    });
  };

  // Bucket a: a future booking with the gap's artist we could pull forward.
  const bringForwardBooking = useMemo(() => {
    if (!gap) return undefined;
    const gapMs = new Date(gap.dayIso).getTime();
    return liveCustomer.bookings
      .map((id) => bookings[id])
      .filter((b): b is NonNullable<typeof b> => Boolean(b))
      .filter(
        (b) =>
          b.artistId === gap.artistId &&
          b.status !== 'cancelled' &&
          b.status !== 'no-show' &&
          b.status !== 'superseded',
      )
      .filter((b) => new Date(b.start).getTime() > gapMs)
      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];
  }, [gap, liveCustomer.bookings, bookings]);

  // Bucket a action: bring the existing booking forward into the gap. Keep-
  // original (15 Jul): the future booking is superseded and a new one is created
  // in the gap, logged with reason gap-fill (passed programmatically, no picker).
  const onChangeDate = () => {
    if (!gap || !bringForwardBooking) return;
    rescheduleBooking({
      bookingId: bringForwardBooking.id,
      reason: 'gap-fill',
      pathway: { mode: 'move', artistId: gap.artistId, newStartIso: gap.startIso },
      by: persona ?? 'system',
      at: clockIso,
    });
    onClose();
  };

  // Bucket b action: place a booking from the paid deposit into the gap.
  const onPlaceFromDeposit = () => {
    if (!gap || !item.depositId) return;
    placeBookingFromDeposit({
      depositId: item.depositId,
      artistId: gap.artistId,
      startIso: gap.startIso,
    });
    onClose();
  };

  // Bucket c action (tv-pt8): a new gap-fill booking, confirmed on the call.
  // Confirm-on-call is fixed (skips the confirmations cadence); the operator's
  // only choice is the deposit method — card over the phone now (instant lock)
  // or a dated + artist link (24h lock).
  const onConfirmGapFill = () => {
    if (!gap) return;
    confirmGapFillOnCall({
      customerId,
      artistId: gap.artistId,
      startIso: gap.startIso,
      endIso: gap.endIso,
      method: gapFillMethod,
      ...(liveCustomer.designIntent?.notes ? { designBrief: liveCustomer.designIntent.notes } : {}),
    });
    onClose();
  };

  // A recently-expired, un-resent deposit link (see queueEngine's pending-deposit
  // generation) gets a one-click resend from the header, right beside the
  // ordinary "Deposit link" button.
  const linkedDeposit = item.depositId ? deposits[item.depositId] : undefined;
  const depositExpired = item.depositExpired || linkedDeposit?.status === 'expired';
  const onResendDeposit = () => {
    if (!item.depositId) return;
    const dep = deposits[item.depositId];
    // Expired links route through the availability check (Prompt I1): same date if
    // free, else pushed to the top of the money queue with the history carried.
    if (dep && isEffectivelyExpired(dep)) {
      const result = routeExpiredResend(item.depositId);
      const push = useUIStore.getState().pushNotification;
      if (result?.outcome === 'resent') {
        push({
          intensity: 'info',
          title: 'Link resent (same date)',
          body: `${customerName(liveCustomer)} · original date still free`,
          ttlMs: 4000,
          customerId,
        });
      } else if (result?.outcome === 'queued') {
        push({
          intensity: 'high',
          title: 'Date taken — pushed to Money',
          body: `${customerName(liveCustomer)} · top of the money queue, rebook needed`,
          ttlMs: 5000,
          customerId,
        });
      }
      onClose();
      return;
    }
    resendDepositLink({ depositId: item.depositId });
    onClose();
  };

  const commitDeclined = () => {
    const note = outcomeNote.trim();
    markSaidNo(customerId);
    if (note) addRapportNote(customerId, note, 'You', clockIso);
    onClose();
  };

  const pinnedArtist = pinnedSlot ? artists[pinnedSlot.artistId] : undefined;
  const pip = stagePip(item);

  // The three working regions are identical in both layouts; only their
  // arrangement changes. Extracting them keeps the classic and focus branches
  // pointing at one source of truth rather than duplicating the markup.
  const briefRailInner = (
    <>
      {gap ? (
        <section data-testid="qwm-gap-fill">
          <p className="label-small-caps text-gold-bright mb-2">Fill this gap</p>
          {item.gapBucket === 'bring-forward' && bringForwardBooking ? (
            <div className="rounded-md border border-gold-bright/50 bg-gold/10 p-3">
              <p className="font-serif text-sm text-cream">
                Booked {simTimeFull(bringForwardBooking.start)} with{' '}
                {artists[gap.artistId]?.displayName ?? gap.artistId}
              </p>
              <p className="mt-2 font-serif text-[12px] text-cream-soft">
                Pull it forward with the move-into-gap button under the availability pane.
              </p>
            </div>
          ) : item.gapBucket === 'deposit' && item.depositId ? (
            <button
              type="button"
              onClick={onPlaceFromDeposit}
              className="btn-primary text-xs"
              data-testid="qwm-place-deposit"
            >
              <CalendarPlus size={12} /> Place into this gap
            </button>
          ) : (
            <div
              className="rounded-md border border-gold-bright/40 bg-gold/[0.06] p-3"
              data-testid="qwm-gapfill-card"
            >
              {/* Confirm-on-call is a fact of a Sweeper gap-fill, not a toggle. */}
              <p className="font-serif text-[12px] text-cream-soft">
                Confirmed on the call: this booking skips the confirmations cadence. The
                confirmation text and waiver still go out.
              </p>
              <p className="label-small-caps text-gold-bright mt-3 mb-1.5">Deposit</p>
              <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Deposit method">
                {([
                  { key: 'phone-eftpos', label: 'Card over the phone now', hint: 'Instant lock' },
                  { key: 'dated-link', label: 'Dated + artist link', hint: '24h lock' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={gapFillMethod === opt.key}
                    onClick={() => setGapFillMethod(opt.key)}
                    data-testid={`qwm-gapfill-method-${opt.key}`}
                    className={clsx(
                      'flex items-center justify-between rounded-md border px-3 py-2 text-left transition',
                      gapFillMethod === opt.key
                        ? 'border-gold-bright bg-gold/10'
                        : 'border-gold-dim/30 hover:border-gold-bright/40',
                    )}
                  >
                    <span className="font-serif text-[13px] text-cream">{opt.label}</span>
                    <span className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
                      {opt.hint}
                    </span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={onConfirmGapFill}
                className="btn-primary w-full justify-center text-xs mt-3"
                data-testid="qwm-gapfill-confirm"
              >
                <CalendarPlus size={12} /> Confirm &amp; book this gap
              </button>
            </div>
          )}
        </section>
      ) : null}

      <CustomerBriefControls customer={liveCustomer} idPrefix="qwm" />

      {/* Sits under the brief it fills: picking a piece writes the fields
                  above and scrolls the rail back up to them. */}
      <section data-testid="qwm-past-bookings">
        <p className="label-small-caps text-gold-bright">Past bookings</p>
        {pieceGroups.some((g) => g.continuable) ? (
          <p className="font-serif text-[12px] text-cream-soft mt-1 mb-2">
            Pick an unfinished piece to continue it - it fills the brief above.
          </p>
        ) : (
          <div className="mb-2" />
        )}
        {pieceGroups.length === 0 && looseBookings.length === 0 ? (
          <p className="font-serif text-sm text-cream-soft">No bookings yet.</p>
        ) : (
          <ul className="space-y-2">
            {/* Grouped by piece, so an unfinished piece is one card the
                        agent can book the next sitting on rather than a run of
                        undifferentiated dates. */}
            {pieceGroups.map(({ piece, latest, continuable }) => {
              const on = continuingPieceId === piece.id;
              return (
                <li
                  key={piece.id}
                  className={clsx(
                    'p-3 rounded-lg border',
                    on ? 'border-gold-bright bg-gold/10' : 'border-gold-dim/15 bg-ink-black',
                  )}
                  data-testid={`qwm-piece-group-${piece.id}`}
                >
                  <p className="font-serif text-sm text-cream">{pieceSessionLabel(piece)}</p>
                  <p className="font-mono text-xs text-cream-soft mt-1">
                    {artists[piece.assignedArtistId]?.displayName ?? piece.assignedArtistId}
                    {latest ? ` · ${simTimeFull(latest.start)}` : ''}
                  </p>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <span
                      className={clsx(
                        'text-[11px] inline-block',
                        continuable ? 'pill-mint' : 'pill-cream',
                      )}
                    >
                      {piece.status.replace(/-/g, ' ')}
                    </span>
                    {continuable ? (
                      <button
                        type="button"
                        onClick={() => toggleContinue(piece)}
                        className={clsx(
                          'rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider transition',
                          on
                            ? 'border-gold-bright bg-gold/15 text-gold-bright'
                            : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/60',
                        )}
                        aria-pressed={on}
                        data-testid={`qwm-continue-${piece.id}`}
                      >
                        {on ? 'Continuing ✓' : 'Continue this piece'}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
            {/* Sittings with no piece attached still have to show up. */}
            {looseBookings.map((b) => (
              <li key={b.id} className="panel-dark p-3">
                <p className="font-serif text-sm text-cream">
                  {artists[b.artistId]?.displayName ?? b.artistId} · {b.studioId.replace('-', ' ')}
                </p>
                <p className="font-mono text-xs text-cream-soft mt-1">{simTimeFull(b.start)}</p>
                <span className="pill-cream text-[11px] mt-2 inline-block">{b.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="qwm-detail">
        <p className="label-small-caps text-gold-bright mb-2">Detail</p>
        {liveCustomer.medicalProfile &&
        (liveCustomer.medicalProfile.allergies?.length ||
          liveCustomer.medicalProfile.conditions?.length ||
          liveCustomer.medicalProfile.medications?.length) ? (
          <div className="flex flex-wrap gap-1.5">
            {[
              ...(liveCustomer.medicalProfile.allergies ?? []),
              ...(liveCustomer.medicalProfile.conditions ?? []),
              ...(liveCustomer.medicalProfile.medications ?? []),
            ].map((v) => (
              <span
                key={v}
                className="rounded-full border border-blood/50 bg-blood/15 text-blood px-2 py-0.5 text-[11px] capitalize"
              >
                {v}
              </span>
            ))}
          </div>
        ) : (
          <p className="font-serif text-sm text-cream-soft">No medical profile on file.</p>
        )}
        <p className="label-small-caps mt-4 mb-1">Lifetime spend</p>
        <p className="font-cinzel text-xl text-gold-bright">{aud(liveCustomer.lifetimeSpendAud)}</p>
      </section>

      <p className="font-serif text-[11px] text-cream-faint leading-relaxed border-t border-gold-dim/15 pt-4">
        Brief + preferences + a picked slot preload the booking when the deposit is paid.
      </p>
    </>
  );

  const availabilityInner = (
    <>
      {/* Controls: week window · artist scope · refresh */}
      <div className="space-y-2" data-testid="qwm-availability-controls">
        <div className="flex items-center justify-between gap-2">
          <div
            className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden"
            data-testid="qwm-week-stepper"
          >
            <button
              type="button"
              onClick={() => stepWeek(-1)}
              className="px-2 py-1 text-cream-soft hover:text-gold-bright transition"
              aria-label="Previous week"
              data-testid="qwm-week-prev"
            >
              <ChevronLeft size={12} />
            </button>
            <span
              className="px-3 py-1 text-[10px] font-inter uppercase tracking-widest text-cream whitespace-nowrap"
              data-testid="qwm-week-range"
            >
              {weekRangeLabel}
            </span>
            <button
              type="button"
              onClick={() => stepWeek(1)}
              className="px-2 py-1 text-cream-soft hover:text-gold-bright transition"
              aria-label="Next week"
              data-testid="qwm-week-next"
            >
              <ChevronRight size={12} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {!gap ? (
              <div
                className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden"
                data-testid="qwm-scope-toggle"
                title="Best fit: preferred style / artist · All: every artist in the studio"
              >
                {(['best', 'all'] as const).map((s) => {
                  const active = scope === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setScope(s)}
                      className={clsx(
                        'px-2 py-1 text-[10px] font-inter uppercase tracking-widest transition',
                        active
                          ? 'bg-gold/15 text-gold-bright'
                          : 'text-cream-soft hover:text-gold-bright',
                      )}
                      data-testid={`qwm-scope-${s}`}
                    >
                      {s === 'best' ? 'Best fit' : 'All'}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <button
              type="button"
              onClick={onRefresh}
              className="btn-quiet text-[10px]"
              data-testid="qwm-refresh"
              title="Re-check availability against current bookings"
            >
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p
            className="font-inter text-[10px] text-cream-faint leading-relaxed"
            data-testid="qwm-scope-note"
          >
            {scopeNote}
          </p>
          <p
            className="font-inter text-[10px] text-cream-faint uppercase tracking-widest whitespace-nowrap"
            data-testid="qwm-updated-stamp"
          >
            Updated {simTimeShort(updatedIso)}
          </p>
        </div>
      </div>
      <ArtistAvailabilityHeatmap
        rows={scopedRows}
        studios={studios}
        selected={selection}
        onSelect={onSelectCell}
        onClear={() => setSelection(null)}
      />
      {selection ? (
        <ArtistDaySchedule
          artistId={selection.artistId}
          dayIso={selection.dayIso}
          onPickWindow={onPickWindow}
        />
      ) : null}
      {pinnedSlot ? (
        <div
          className="rounded-lg border border-gold-bright/50 bg-gold/10 p-3"
          data-testid="qwm-pinned-slot"
        >
          <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
            Discussed slot · grounding
          </p>
          <p className="font-serif text-sm text-cream mt-1">
            {pinnedArtist?.displayName ?? pinnedSlot.artistId} · {simTimeFull(pinnedSlot.startIso)}{' '}
            – {simTimeShort(pinnedSlot.endIso)}
          </p>
          {/* The Send-deposit action surfaces right where the slot lands,
                      not only in the header - it opens the deposit window grounded
                      to this exact artist + window. Gap mode keeps its own CTA below. */}
          {!gap ? (
            <button
              type="button"
              onClick={() => setDepositOpen(true)}
              className="btn-primary w-full mt-3 justify-center"
              data-testid="qwm-slot-send-deposit"
            >
              <Wallet size={13} /> Send deposit · grounded to this slot
            </button>
          ) : null}
        </div>
      ) : null}
      {/* Bring-forward: the primary action sits full-width under the
                  availability pane, right where the operator lands the slot. */}
      {gap && item.gapBucket === 'bring-forward' && bringForwardBooking ? (
        <button
          type="button"
          onClick={onChangeDate}
          className="btn-primary w-full py-3 text-base"
          data-testid="qwm-change-date"
        >
          <CalendarClock size={14} /> Move into this gap · {simTimeFull(gap.startIso)}
        </button>
      ) : null}
    </>
  );

  const conversationBody = (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
        {callForThisCustomer ? <LiveCallBanner call={callForThisCustomer} /> : null}
        {/* Six facts up top; the transcript is a click away rather than
                    the first thing the agent has to read. */}
        <ConversationGlance customer={liveCustomer} />
        <SuggestedReplyCard
          customer={liveCustomer}
          channel={suggestChannel}
          onEdit={setComposerDraft}
        />
      </div>
      <CustomerComposer
        customer={liveCustomer}
        host="queue"
        initialChannel={suggestChannel}
        initialDraft={composerDraft}
      />
    </>
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-cream/10 backdrop-blur-sm"
        onClick={() => requestCloseRef.current()}
        data-testid="queue-work-modal"
      >
        <motion.div
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.98, opacity: 0 }}
          className={clsx(
            'relative panel bg-ink-deep w-full h-[90vh] rounded-xl flex flex-col overflow-hidden shadow-gold-glow',
            layout === 'focus' ? 'max-w-[1180px]' : 'max-w-[1600px]',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header (full width) */}
          <header className="flex items-start justify-between gap-4 border-b border-gold-dim/20 px-6 py-4">
            <div className="flex items-start gap-3 min-w-0">
              {/* Close leads the header rather than trailing the action cluster,
                  so the way out is where the eye starts. */}
              <button
                type="button"
                onClick={() => requestCloseRef.current()}
                className="btn-quiet shrink-0 mt-1"
                aria-label="Close"
                data-testid="qwm-close"
              >
                <X size={14} />
              </button>
              <div className="min-w-0">
                <h2 className="heading-display text-2xl text-cream truncate">
                  {customerName(liveCustomer)}
                </h2>
                <p className="font-serif text-sm text-cream-soft mt-0.5">
                  {cohortLine(item.cohort)}
                </p>
                {pip ? (
                  <span
                    className={clsx(
                      'inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border',
                      item.stage === 'red'
                        ? 'border-blood/50 bg-blood/10 text-blood'
                        : 'border-gold-bright/50 bg-gold/10 text-gold-bright',
                    )}
                    data-testid="qwm-stage-pip"
                  >
                    {pip}
                  </span>
                ) : null}
              </div>
            </div>
            {/* Call lives on the composer, and deposits are taken from the
                availability pane once a slot is pinned, so the header keeps only
                what it alone can do. */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Layout switch: offer the conversation-first arrangement as an
                  option without disturbing the built three-pane default. */}
              <div
                className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden"
                data-testid="qwm-layout-toggle"
                title="Modal layout — Classic (three columns) or Focus (conversation-first)"
              >
                {(['classic', 'focus'] as const).map((l) => {
                  const active = layout === l;
                  return (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setLayout(l)}
                      className={clsx(
                        'px-2.5 py-1 text-[10px] font-inter uppercase tracking-widest transition',
                        active
                          ? 'bg-gold/15 text-gold-bright'
                          : 'text-cream-soft hover:text-gold-bright',
                      )}
                      aria-pressed={active}
                      data-testid={`qwm-layout-${l}`}
                    >
                      {l === 'classic' ? 'Classic' : 'Focus'}
                    </button>
                  );
                })}
              </div>
              {depositExpired ? (
                <button
                  type="button"
                  onClick={onResendDeposit}
                  className="btn-ghost text-xs border-gold-bright/60 text-gold-bright"
                  data-testid="qwm-resend-deposit"
                  title="Deposit link expired - send a fresh one"
                >
                  <RefreshCw size={12} /> Resend deposit link
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => navigate(`/customer/${customerId}`)}
                className="btn-quiet text-xs"
                data-testid="qwm-open-record"
              >
                <ExternalLink size={12} /> Open full record
              </button>
            </div>
          </header>

          {layout === 'focus' ? (
            /* Focus: the conversation gets the room; the brief and availability
               share one panel behind a switch, so the form and the grid never
               compete for the same glance. */
            <div className="flex flex-1 min-h-0">
              <section className="flex-1 min-w-0 flex flex-col min-h-0 border-r border-gold-dim/15">
                {conversationBody}
              </section>
              <aside className="w-[460px] shrink-0 flex flex-col min-h-0">
                <div className="px-5 pt-4 pb-3 border-b border-gold-dim/15">
                  <div
                    className="inline-flex w-full rounded-md border border-gold-dim/30 overflow-hidden"
                    role="tablist"
                    data-testid="qwm-tool-tabs"
                  >
                    {(['brief', 'availability'] as const).map((t) => {
                      const active = toolTab === t;
                      return (
                        <button
                          key={t}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setToolTab(t)}
                          className={clsx(
                            'flex-1 px-3 py-2 text-[10px] font-inter uppercase tracking-widest transition',
                            active
                              ? 'bg-gold/15 text-gold-bright'
                              : 'text-cream-soft hover:text-gold-bright',
                          )}
                          data-testid={`qwm-tool-${t}`}
                        >
                          {t === 'brief' ? 'Brief' : 'Availability'}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <section ref={briefRailRef} className="flex-1 overflow-y-auto px-5 py-5">
                  {toolTab === 'brief' ? (
                    <div className="space-y-6">
                      {briefRailInner}
                      <button
                        type="button"
                        onClick={() => setToolTab('availability')}
                        className="btn-ghost w-full justify-center text-xs"
                        data-testid="qwm-find-slot"
                      >
                        Find a slot <ChevronRight size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">{availabilityInner}</div>
                  )}
                </section>
              </aside>
            </div>
          ) : (
            /* Classic: the built three-pane layout, unchanged. */
            <div className="flex flex-1 min-h-0">
              <aside
                ref={briefRailRef}
                className="w-[380px] shrink-0 border-r border-gold-dim/15 overflow-y-auto px-5 py-5 space-y-6"
              >
                {briefRailInner}
              </aside>
              <aside className="w-[420px] shrink-0 border-r border-gold-dim/15 overflow-y-auto px-5 py-5 space-y-4">
                {availabilityInner}
              </aside>
              <section className="flex-1 min-w-0 flex flex-col min-h-0">{conversationBody}</section>
            </div>
          )}

          {/* Outcome step (inline sheet) */}
          <AnimatePresence>
            {showOutcome ? (
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 24, opacity: 0 }}
                className="absolute inset-x-0 bottom-0 border-t border-gold-bright/40 bg-ink-deep px-6 py-4 shadow-gold-glow"
                data-testid="qwm-outcome"
              >
                <p className="label-small-caps text-gold-bright mb-3">How did it go?</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={commitDeclined}
                    className="btn-ghost text-xs border-blood text-blood hover:bg-blood/10"
                    data-testid="qwm-outcome-said-no"
                  >
                    Declined / not interested
                  </button>
                </div>
                <p className="font-serif text-[11px] text-cream-faint mt-2">
                  No answer or no reply advances the sequence automatically. A booking clears it.
                </p>
                <textarea
                  value={outcomeNote}
                  onChange={(e) => setOutcomeNote(e.target.value)}
                  placeholder="Optional note…"
                  rows={2}
                  data-testid="qwm-outcome-note"
                  className="mt-3 w-full rounded-md border border-gold-dim/30 bg-ink-black px-3 py-2 font-serif text-sm text-cream placeholder:text-cream-faint resize-none focus:border-gold-bright focus:outline-none"
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      <DepositLinkModal
        open={depositOpen}
        customer={liveCustomer}
        onClose={() => setDepositOpen(false)}
        discussedSlot={pinnedSlot ?? undefined}
        initialPieceName={liveCustomer.designIntent?.notes}
      />
    </>
  );
}
